/**
 * `/parent` — the Parent Portal dashboard (P-SCH-V1, ADR-0103).
 *
 * A guardian's at-a-glance view of the selected child: a child-selector pill
 * row (when linked to ≥ 2 students), the child header, and a KPI strip rolled
 * up from the child's attendance + recent results (`learning.learner.guardian.
 * {childAttendance,childResults}`). Strictly read-only. The student app's
 * `<LearnShell>` is NOT used here — every parent page renders inside
 * `<ParentRoute>` (Phase 3, ADR-0103), which composes the auth gate, the
 * `familyPortalEnabled` feature gate (off ⇒ `/home`), and `<ParentShell>`.
 * The `/` entry redirect routes a signed-in guardian here.
 */
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { inferRouterOutputs } from '@trpc/server';
import { CalendarCheck, CheckCircle2, Clock, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, KpiCard, Skeleton } from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { ParentRoute } from '../components/parent-portal/parent-shell.js';
import {
  ChildSelector,
  childInitials,
  childLabel,
  relationshipLabel,
  useSelectedChild,
  type ParentChild,
} from '../components/parent-portal/child-selector.js';
import {
  ChildHeaderCard,
  ErrorCard,
  NoChildrenState,
  ParentHero,
} from '../components/parent-portal/parent-shared.js';

export const Route = createFileRoute('/parent/')({
  component: ParentDashboardPage,
});

type AttendanceRow =
  inferRouterOutputs<AppRouter>['learning']['learner']['guardian']['childAttendance']['rows'][number];
type ResultRow =
  inferRouterOutputs<AppRouter>['learning']['learner']['guardian']['childResults']['rows'][number];

function ParentDashboardPage(): React.JSX.Element {
  return (
    <ParentRoute title="Dashboard">
      <ParentDashboardContent />
    </ParentRoute>
  );
}

function ParentDashboardContent(): React.JSX.Element {
  const { query, children, selected, setSelected } = useSelectedChild();

  return (
    <div className="space-y-6">
      <ParentHero
        title="Family overview"
        subtitle="A clear snapshot of how your student is progressing — attendance and recent results."
      />

      {query.isPending ? (
        <DashboardSkeleton />
      ) : query.isError ? (
        <ErrorCard
          message="We couldn’t load your linked students. Please check your connection and try again."
          onRetry={() => void query.refetch()}
        />
      ) : children.length === 0 ? (
        <NoChildrenState />
      ) : (
        <div className="space-y-6">
          {selected ? (
            <ChildHeaderCard
              initials={childInitials(selected)}
              name={childLabel(selected)}
              relationship={relationshipLabel(selected.relationship)}
            />
          ) : null}
          <ChildSelector children={children} selected={selected} onSelect={setSelected} />
          {selected ? <KpiStrip child={selected} /> : null}
        </div>
      )}
    </div>
  );
}

/** The KPI strip for one child — attendance rate / present / late / avg score. */
function KpiStrip({ child }: { child: ParentChild }): React.JSX.Element {
  const studentUserId = child.studentUserId;
  const attendanceQ = trpc.learning.learner.guardian.childAttendance.useQuery({ studentUserId });
  const resultsQ = trpc.learning.learner.guardian.childResults.useQuery({ studentUserId });

  const attendanceRows = attendanceQ.data?.rows ?? [];
  const resultRows = resultsQ.data?.rows ?? [];

  const stats = React.useMemo(
    () => computeStats(attendanceRows, resultRows),
    [attendanceRows, resultRows],
  );

  return (
    <div key={studentUserId} className="ls-stagger grid grid-cols-2 gap-4 sm:grid-cols-4">
      <KpiCard
        accent="success"
        icon={CheckCircle2}
        label="Attendance"
        value={stats.attendanceTotal > 0 ? `${stats.attendanceRate}%` : '—'}
        hint={
          stats.attendanceTotal > 0
            ? `${stats.presentCount + stats.lateCount} of ${stats.attendanceTotal} sessions`
            : 'No sessions yet'
        }
        isLoading={attendanceQ.isPending}
      />
      <KpiCard
        accent="primary"
        icon={CalendarCheck}
        label="Present"
        value={String(stats.presentCount)}
        isLoading={attendanceQ.isPending}
      />
      <KpiCard
        accent={stats.lateCount > 0 ? 'warning' : 'primary'}
        icon={Clock}
        label="Late"
        value={String(stats.lateCount)}
        isLoading={attendanceQ.isPending}
      />
      <KpiCard
        accent={stats.gradedCount > 0 ? kpiTone(stats.averageScore) : 'primary'}
        icon={TrendingUp}
        label="Average score"
        value={stats.gradedCount > 0 ? `${stats.averageScore}%` : '—'}
        hint={stats.gradedCount > 0 ? `${stats.gradedCount} graded` : 'No graded results'}
        isLoading={resultsQ.isPending}
      />
    </div>
  );
}

/** Roll up attendance + result rows into the KPI numbers. */
function computeStats(
  attendance: AttendanceRow[],
  results: ResultRow[],
): {
  attendanceRate: number;
  attendanceTotal: number;
  presentCount: number;
  lateCount: number;
  averageScore: number;
  gradedCount: number;
} {
  const presentCount = attendance.filter((r) => r.status === 'present').length;
  const lateCount = attendance.filter((r) => r.status === 'late').length;
  const attendanceTotal = attendance.length;
  // Present + late both count as "showed up" toward the attendance rate.
  const attendanceRate =
    attendanceTotal > 0 ? Math.round(((presentCount + lateCount) / attendanceTotal) * 100) : 0;

  const graded = results.filter((r) => r.status === 'graded' && r.scorePercent != null);
  const gradedCount = graded.length;
  const averageScore =
    gradedCount > 0
      ? Math.round(graded.reduce((sum, r) => sum + (r.scorePercent ?? 0), 0) / gradedCount)
      : 0;

  return { attendanceRate, attendanceTotal, presentCount, lateCount, averageScore, gradedCount };
}

/** KpiCard accent tone for the average-score card. */
function kpiTone(score: number): 'success' | 'warning' | 'destructive' {
  if (score >= 80) return 'success';
  if (score >= 50) return 'warning';
  return 'destructive';
}

function DashboardSkeleton(): React.JSX.Element {
  return (
    <div className="space-y-6">
      <Card className="card-soft">
        <CardContent className="flex items-center gap-4 p-5">
          <Skeleton className="size-14 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-24" />
          </div>
        </CardContent>
      </Card>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Card key={i} className="card-soft">
            <CardHeader className="pb-2">
              <CardTitle className="sr-only">Loading</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 p-5 pt-0">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
